import { useEffect, useState } from 'react'
import {
  Button,
  Grid,
  Input,
  Separator,
  useToastAlert,
} from '@design-systems/canvas-ui'
import _ from 'lodash'
import Icon, {
  PlusCircleIcon,
  MinusCircleIcon,
} from '@design-systems/react-icons'
import { renderControlView, isBuildControl } from './centralControlBuilder'
import { SectionWrapper, extractPhoneNumberProps } from './'
import {
  uploadFiles,
  downloadFile,
  deleteFile,
  deleteSpecificComponentRow,
} from '../../services/lcmService'
import { renderRFI3QuestionWindow } from '../../common/rfiHelper'
import {
  CONTROL_COMPOSITE_UNIT,
  CONTROL_TYPE_FILEDOWNLOAD,
  CONTROL_TYPE_FILEUPLOAD,
  CONTROL_TYPE_MULTI_AUTOCOMPLETE,
  CONTROL_TYPE_MULTI_AUTOCOMPLETE_HIERARCHY,
  CONTROL_TYPE_MULTI_AUTOCOMPLETE_OTHERS,
  CONTROL_TYPE_STRENGTH_ASSESSMENT,
  FIELD_FABRIC_PRIMARY_TYPE,
  FIELD_FABRIC_STRENGTH_ASSESSMENT,
  FIELD_SUBSTRATE_SUB_TYPE,
  FIELD_SUBSTRATE_TYPE,
  FIELD_TRIM_LIST_PRODUCT_SPECIFICATION,
  FIELD_TRIM_MATERIAL_TYPE,
  FIELD_TRIM_SUB_TYPE,
  FIELD_TRIM_TYPE,
  FINISHED_GOOD_ITEM_TYPE,
  FINISHED_GOOD_PRODUCT_SUB_TYPE,
  FINISHED_GOOD_PRODUCT_TYPE,
} from '../../common/commonConstants'
import { dotSnakeToDotCamel } from '../../common/helper'

const BUTTON = 'BUTTON'
const TEXT = 'TEXT'
const ICON = 'icon'
const DELETE_SPACE = 'delete_space'
const SOURCE_CONTROL = 'CONTROL'
const SOURCE_SUBSECTION = 'SUBSECTION'
const CONTROL_AUTOCOMPLETE_HIERARCHY_UPPER = 'AUTOCOMPLETE_HIERARCHY'

/**
 * Logic to dynamically generate any section following one of the formats
 * Type 1 : Section is only array of controls (No row based view)
 * Type 2 : Section is based on array of subsections - each row is a self replicating unit (1 unit is array of controls)
 * Type 3 : Extenstion of type 2 - each row can contain a composite control (Inside row repeater) that itself has many rows of controls
 * @param {*} props
 * @returns
 */
const DynamicSectionBuilder = (props) => {
  const makeToast = useToastAlert()
  const [sectionData, setSectionData] = useState(null)

  //Add more details (Section level)
  const [topAddEligibleSubSection, setTopAddEligibleSubSection] = useState(null)

  //Trigger save
  const [isTriggerSave, setIsTriggerSave] = useState(false)

  //File Upload zone
  const [fileActionInProgressKey, setFileActionInProgressKey] = useState(null)
  const [fileActionFailedKey, setFileActionFailedKey] = useState(null)
  const [isRefreshRequired, setIsRefreshRequired] = useState(false)

  //#region UseEffects

  useEffect(() => {
    setTopAddEligibleSubSection(null)
    //Set the top addable subsection
    if (props.sectionTemplate.sub_sections) {
      let topAddSubSection = props.sectionTemplate.sub_sections.filter(
        (eachSubSection) =>
          eachSubSection.add_more?.type?.toUpperCase() === BUTTON,
      )
      if (topAddSubSection.length > 0) {
        setTopAddEligibleSubSection(topAddSubSection[0])
      }
    }
  }, [props.sectionTemplate])

  useEffect(() => {
    if (!_.isEqual(sectionData, props.sectionData)) {
      setSectionData(props.sectionData)
    }
  }, [props.sectionData]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isTriggerSave && !_.isEqual(props.sectionData, sectionData)) {
      props.saveRfiPayload(
        sectionData,
        props.sectionTemplate.uid,
        isRefreshRequired,
      )
      setIsTriggerSave(false)
    }
  }, [isTriggerSave]) // eslint-disable-line react-hooks/exhaustive-deps

  //#endregion

  /**
   * Conctrol value update callback
   * @param {*} newValue
   * @param {*} updateMetadata
   * @param {*} refreshRequired
   */
  const onInputChangeHandler = (newValue, updateMetadata, refreshRequired) => {
    //Update coming from Control
    if (updateMetadata.inputMetadata?.source === SOURCE_CONTROL) {
      if (updateMetadata.type?.toUpperCase() === 'PHONE_NUMBER_INPUT') {
        let phoneNumberFields = extractPhoneNumberProps(updateMetadata)
        setSectionData({
          ...sectionData,
          [phoneNumberFields.countryCodeProp]: newValue.countryCode,
          [phoneNumberFields.phoneNumberProp]: newValue.phoneNumber,
        })
      } else {
        setSectionData({
          ...sectionData,
          [updateMetadata.uid]: newValue,
        })
      }
    } else {
      //Update coming from Subsection
      let newSectionData = {}
      if (updateMetadata.type?.toUpperCase() === 'PHONE_NUMBER_INPUT') {
        let phoneNumberFields = extractPhoneNumberProps(updateMetadata)
        newSectionData = sectionData[
          updateMetadata.inputMetadata.subSectionReferenceListKey
        ].map((eachRow, rowIndex) => {
          return rowIndex === updateMetadata.inputMetadata.index
            ? {
                ...eachRow,
                [phoneNumberFields.countryCodeProp]: newValue.countryCode,
                [phoneNumberFields.phoneNumberProp]: newValue.phoneNumber,
              }
            : eachRow
        })
      } else {
        //Either type 2 OR 3 control
        if (updateMetadata.inputMetadata?.subControlReferenceListKey) {
          //Type 3 - composite control (subcontrol control)
          newSectionData = sectionData[
            updateMetadata.inputMetadata.subSectionReferenceListKey
          ].map((eachRow, rowIndex) => {
            if (rowIndex !== updateMetadata.inputMetadata.index) {
              return eachRow
            } else {
              let newSubRows = [
                ...eachRow[
                  updateMetadata.inputMetadata.subControlReferenceListKey
                ],
              ]
              newSubRows = newSubRows.map((eachSubRow, subRowIndex) => {
                if (
                  subRowIndex === updateMetadata.inputMetadata.subControlIndex
                ) {
                  let valueToUpdate = updateStateValueByControl(
                    updateMetadata,
                    newValue,
                  )
                  return {
                    ...eachSubRow,
                    ...valueToUpdate,
                  }
                } else {
                  return eachSubRow
                }
              })

              return {
                ...eachRow,
                [updateMetadata.inputMetadata.subControlReferenceListKey]:
                  newSubRows,
              }
            }
          })
        } else {
          //Type 2 - subcontrol control
          newSectionData = sectionData[
            updateMetadata.inputMetadata.subSectionReferenceListKey
          ].map((eachRow, rowIndex) => {
            if (rowIndex === updateMetadata.inputMetadata.index) {
              let valueToUpdate = updateStateValueByControl(
                updateMetadata,
                newValue,
              )
              return { ...eachRow, ...valueToUpdate }
            } else {
              return eachRow
            }
          })
        }
      }

      setSectionData({
        ...sectionData,
        [updateMetadata.inputMetadata.subSectionReferenceListKey]:
          newSectionData,
      })
    }

    //refresh screen
    if (refreshRequired) {
      setIsRefreshRequired(true)
    }
  }

  /**
   * Update the state value based on control - common place to handle state updates by control types
   * Purpose: Some controls on change might need other dependent states to be updated eg Hierarchy dropdowns
   * @param {*} updateMetadata
   * @param {*} newValue
   * @returns
   */
  const updateStateValueByControl = (updateMetadata, newValue) => {
    if (
      !newValue &&
      updateMetadata.type?.toUpperCase() ===
        CONTROL_AUTOCOMPLETE_HIERARCHY_UPPER
    ) {
      switch (updateMetadata.uid) {
        case FIELD_TRIM_TYPE:
          return {
            [FIELD_TRIM_TYPE]: newValue,
            [FIELD_TRIM_SUB_TYPE]: '',
            [FIELD_TRIM_MATERIAL_TYPE]: [],
            [FIELD_TRIM_LIST_PRODUCT_SPECIFICATION]: [{}],
          }
        case FIELD_SUBSTRATE_TYPE:
          return {
            [FIELD_SUBSTRATE_TYPE]: newValue,
            [FIELD_SUBSTRATE_SUB_TYPE]: [],
          }
        case FINISHED_GOOD_PRODUCT_TYPE:
          return {
            [FINISHED_GOOD_PRODUCT_TYPE]: newValue,
            [FINISHED_GOOD_PRODUCT_SUB_TYPE]: '',
            [FINISHED_GOOD_ITEM_TYPE]: [],
          }
        case FINISHED_GOOD_PRODUCT_SUB_TYPE:
          return {
            [FINISHED_GOOD_PRODUCT_SUB_TYPE]: newValue,
            [FINISHED_GOOD_ITEM_TYPE]: [],
          }
        case FIELD_FABRIC_PRIMARY_TYPE:
          return {
            [FIELD_FABRIC_PRIMARY_TYPE]: newValue,
            [FIELD_FABRIC_STRENGTH_ASSESSMENT]: { segments: [] },
          }
        default:
          return { [updateMetadata.uid]: newValue }
      }
    } else {
      return { [updateMetadata.uid]: newValue }
    }
  }

  /**
   * Render section content
   * Type 1. Section has control(s) directly
   * Type 2. Section has sub-section(s) each containing array of control(s)
   * Type 3. Section has sub-section(s) each containing 1/many composite controls (controls with multiple rows of controls inside them)
   * @returns
   */
  const renderSectionContent = () => {
    let sectionContent

    if (sectionData) {
      if (props.sectionTemplate?.controls) {
        //Type 1
        sectionContent = renderControls(props.sectionTemplate?.controls)
      } else if (props.sectionTemplate?.sub_sections) {
        //Type 2 OR 3
        sectionContent = renderSubSections()
      }
    }

    return props.isRfi3
      ? renderRFI3QuestionWindow(
          props.isRfi3Readonly,
          sectionContent,
          props.sectionData?.rfi3_answers,
          props.sectionTemplate,
          props.rfiMasterData,
          (sectionRfi3Answers) =>
            setSectionData({
              ...sectionData,
              rfi3_answers: sectionRfi3Answers,
            }),
          props.supplierId,
          props.locationId,
          props.manufacturingTypeId,
          props.sectionData?.rfi3_files,
          (sectionRfiFiles) => {
            setIsTriggerSave(true)
            setSectionData({
              ...sectionData,
              rfi3_files: sectionRfiFiles,
            })
          },
          props.errorCache,
        )
      : sectionContent
  }

  /**
   * Render controls (Type 1 of template sections)
   * @param {*} allControls
   * @returns
   */
  const renderControls = (allControls) => {
    return (
      <div className={props.isHeaderSection ? 'hc8-mt-3x' : ''}>
        {buildControlView(
          SOURCE_CONTROL, //source = control (Type1)
          props.sectionPath,
          null, //RowIndex = null
          allControls,
          null, //subSectionId = null
        )}
      </div>
    )
  }

  /**
   * Render subsections and then controls inside them (Type 2 OR Type 3 of template sections)
   * Steps:
   * 1. Parse the template to check all subsections specified (each subsection will specify a model)
   * 2. For each subsection get the real data using the uid from subsection template(will be a list)
   * 3. For all rows of data in (2) replicate the model in (1)
   * @returns
   */
  const renderSubSections = () => {
    let allSubsections = []
    //1. Parse each subsection [For each subsection there can be multiple rows of data]
    props.sectionTemplate?.sub_sections.forEach(
      (eachSubsectionTemplate, subSectionIndex) => {
        //2. Get real data - for this subsection
        let subSectionModel = eachSubsectionTemplate.data
        let subSectionListData = sectionData[eachSubsectionTemplate.uid]
        let sectionTemplatePath = `${props.sectionPath}.${eachSubsectionTemplate.uid}`

        //3. Build all rows of this subsection
        let subSectionRows = []
        //For each list item -> multiple rows inside this subsection
        subSectionListData?.forEach(
          (eachSubSectionObject, subSectionRowIndex) => {
            //Mutate the "add-more" data and setup the IDs (showing row header for each row)
            let mutatedEachSubsection = Object.assign({}, subSectionModel)
            mutatedEachSubsection.key =
              eachSubsectionTemplate.uid + (subSectionRowIndex + 1)
            if (mutatedEachSubsection.row_header) {
              mutatedEachSubsection.row_header = `${
                mutatedEachSubsection.row_header
              } ${subSectionRowIndex + 1}`
            }

            let row = buildControlView(
              SOURCE_SUBSECTION, //source = subsection
              sectionTemplatePath, //Section template path (w/o index)
              subSectionRowIndex, //index of this subsection row
              mutatedEachSubsection.controls, //allControls in 1 row
              eachSubSectionObject, //single row data
              eachSubsectionTemplate,
            )

            subSectionRows.push(
              <div className="hc8-mt-2x">
                {/* Each Subsection header + Remove button */}
                {mutatedEachSubsection.row_header && (
                  <Grid.Container className="row-centre">
                    <Grid.Item size={{ xs: 10 }}>
                      <Input.Label className="hc8-fs-150 font-weight-500">
                        {mutatedEachSubsection.row_header}
                      </Input.Label>
                    </Grid.Item>
                    <Grid.Item size={{ xs: 2 }} className="align-self-right">
                      {/* Conditionally show Remove (row level) */}
                      {eachSubsectionTemplate.remove &&
                        eachSubsectionTemplate.remove.type?.toUpperCase() ===
                          BUTTON &&
                        ((eachSubsectionTemplate.remove.first_row_mandatory &&
                          subSectionRowIndex !== 0) ||
                          !eachSubsectionTemplate.remove
                            .first_row_mandatory) && (
                          <Button
                            onClick={() => {
                              deleteSubsectionRow(
                                subSectionRowIndex,
                                eachSubsectionTemplate,
                                sectionTemplatePath,
                              )
                            }}
                            variant="secondary"
                            className="section-button"
                          >
                            {eachSubsectionTemplate.remove.label}
                          </Button>
                        )}
                    </Grid.Item>
                  </Grid.Container>
                )}
                <div>{row}</div>

                {/* Divider till last but 1 row OR margin */}
                {!eachSubsectionTemplate.metadata?.is_mask_divider &&
                  subSectionRowIndex < subSectionListData.length - 1 && (
                    <Separator className="hc8-mv-3x" />
                  )}

                {eachSubsectionTemplate.metadata?.is_mask_divider && (
                  <div className="hc8-mt-2x"></div>
                )}
              </div>,
            )
          },
        )
        allSubsections.push(
          <div>
            {eachSubsectionTemplate.sub_section_name && (
              <div
                className={`row-centre ${
                  subSectionIndex === 0 ? 'hc8-mt-2x' : 'hc8-mt-6x'
                }`}
              >
                <Input.Label className="hc8-fs-150 hc8-mb-3x">
                  {eachSubsectionTemplate.sub_section_name}
                </Input.Label>
              </div>
            )}

            {subSectionRows}
            {renderAddMoreSubSection(eachSubsectionTemplate.uid)}
          </div>,
        )
      },
    )
    return <div>{allSubsections}</div>
  }

  //#region Type2-(Subsections)

  /**
   * Delete a specific row inside a subsection
   * For subsection row -> w/o fileUpload : call autosave
   *                    -> with fileUpload : call row delete API (that takes care of cleaning file from TOSS)
   * @param {*} subSectionRowIndex
   * @param {*} subSectionTemplate
   * @param {*} sectionTemplatePath
   */
  const deleteSubsectionRow = async (
    subSectionRowIndex,
    subSectionTemplate,
    sectionTemplatePath,
  ) => {
    let subSectionReferenceKey = subSectionTemplate.uid
    let isCallDeleteRowApi =
      subSectionTemplate.remove.delete_component_row || false
    if (!isCallDeleteRowApi) {
      //a. Section w/o fileUpload(not isCallDeleteRowApi) -> remove row -> autosave
      removeSubsectionRowState(subSectionReferenceKey, subSectionRowIndex)
      setIsTriggerSave(true)
    } else {
      //b. Section with fileUpload(not isCallDeleteRowApi) -> call DELETE api
      let rowPath = `dynamicSections.${dotSnakeToDotCamel(sectionTemplatePath)}`
      let deleteResponse = await deleteSubsectionRowUnit(
        rowPath,
        subSectionRowIndex,
      )
      if (deleteResponse) {
        removeSubsectionRowState(subSectionReferenceKey, subSectionRowIndex)
      }
    }
  }

  /**
   * Soft remove subsection row (that is to be deleted) from state
   * @param {*} subSectionReferenceKey
   * @param {*} subSectionRowIndex
   */
  const removeSubsectionRowState = (
    subSectionReferenceKey,
    subSectionRowIndex,
  ) => {
    setSectionData({
      ...sectionData,
      [subSectionReferenceKey]: sectionData[subSectionReferenceKey].filter(
        (eachRow, rowindex) => rowindex !== subSectionRowIndex,
      ),
    })
  }

  /**
   * Hard-Delete the subsection row.
   * Calls the generic DELETE API to hard-delete the specific index (of the subsection) from DB
   * NOTE: Use this method only when subseciton has fileUploads - this ensures its cleaned up from TOSS
   * @param {*} componentPath - identifies the subsection dot-path
   * @param {*} deleteRowIndex - index in the subsection to delete
   * @returns
   */
  const deleteSubsectionRowUnit = async (componentPath, deleteRowIndex) => {
    try {
      await deleteSpecificComponentRow(
        props.rfiType, //rfi type
        props.supplierId,
        props.locationId,
        componentPath, //row dot path
        deleteRowIndex, //delte row index
        props.manufacturingTypeId, //manufacturing type id
      )
      return true
    } catch (error) {
      return false
    }
  }

  //#endregion

  /**
   * Build control view (For all section types 1/2/3)
   * @param {*} source
   * @param {*} sectionPath
   * @param {*} subSectionRowIndex
   * @param {*} allControls
   * @param {*} subSectionData
   * @param {*} subSectionTemplate
   * @returns
   */
  const buildControlView = (
    source, // CONTROL / SUBSECTION,
    sectionPath,
    subSectionRowIndex,
    allControls,
    subSectionData = null,
    subSectionTemplate = null,
  ) => {
    let allRows = []
    let sectionPathIndex =
      subSectionRowIndex !== null
        ? `${sectionPath}[${subSectionRowIndex}]`
        : sectionPath

    //NOTE: Each row can have many controls that are distributed not in 1 line but several lines of controls
    //Control attributes have row & column row 0 is 1st line, row 1 is 2nd line
    //All together they are 1 row (in the subsection)
    if (allControls) {
      ;[...new Set(allControls.map((item) => item.row))]
        .sort((a, b) => a - b)
        .forEach((eachRow, eachRowIndex) => {
          let thisRowControls = allControls.filter(
            (eachControl) => eachControl.row === eachRow,
          )

          let thisRowControlsCells = []
          thisRowControls.forEach((eachRowControl, colIndex) => {
            //a. Add control metadata (eg source, path, errorCache, subSectionReferenceListKey(uid))
            let mutatedRowControl = Object.assign({}, eachRowControl)

            let controlMetadata = {}
            controlMetadata.source = source
            controlMetadata.errorCache = props.errorCache
            controlMetadata.sectionPath = sectionPathIndex
            controlMetadata.isreadOnly = props.isReadonly
            controlMetadata.sectionUid = props.sectionTemplate?.uid
            //FileHandler metadata
            if (
              mutatedRowControl.type?.toLowerCase() === CONTROL_TYPE_FILEUPLOAD
            ) {
              //File Upload state handling
              controlMetadata.fileActionKey = `${sectionPathIndex}.${mutatedRowControl.uid}`
              controlMetadata.isFileActionInProgress =
                fileActionInProgressKey === controlMetadata.fileActionKey
              controlMetadata.isFileActionFailed =
                fileActionFailedKey === controlMetadata.fileActionKey
              controlMetadata.uploadFile = uploadSectionFile
              controlMetadata.downloadFile = downloadSectionFile
              controlMetadata.deleteFile = deleteSectionFile
            } else if (
              mutatedRowControl.type?.toLowerCase() ===
              CONTROL_TYPE_FILEDOWNLOAD
            ) {
              //File Download state handling
              controlMetadata.downloadFile = downloadSectionFile
            }

            if (source === SOURCE_SUBSECTION) {
              controlMetadata.subSectionReferenceListKey =
                subSectionTemplate.uid
              controlMetadata.index = subSectionRowIndex
            }
            mutatedRowControl.metadata = controlMetadata

            switch (eachRowControl.type?.toLowerCase()) {
              case DELETE_SPACE: //This is for inline delete (last element will be a delete icon)
                thisRowControlsCells.push(
                  <Grid.Item
                    size={{ xs: eachRowControl.width }}
                    className="row-top"
                  >
                    {subSectionTemplate &&
                      subSectionTemplate.remove &&
                      subSectionTemplate.remove?.type?.toLowerCase() === ICON &&
                      ((subSectionTemplate.remove?.first_row_mandatory &&
                        subSectionRowIndex !== 0) ||
                        !subSectionTemplate.remove?.first_row_mandatory) &&
                      colIndex === thisRowControls.length - 1 && (
                        <Button
                          data-testid={`icon-delete-row-${subSectionTemplate.uid}-${subSectionRowIndex}`}
                          key={`icon-delete-row-${subSectionTemplate.uid}-${subSectionRowIndex}`}
                          className="hc8-mt-6x"
                          onClick={() => {
                            deleteSubsectionRow(
                              subSectionRowIndex,
                              subSectionTemplate,
                              sectionPath,
                            )
                          }}
                          variant="ghost"
                          size="sm"
                          isIconOnly
                          aria-label="delete row"
                        >
                          <Icon svg={MinusCircleIcon} />
                        </Button>
                      )}
                  </Grid.Item>,
                )
                break
              case CONTROL_COMPOSITE_UNIT: //Composite control (Type 3) Control has rows inside it each of a model(array of controls)
                isBuildControl(eachRowControl, subSectionData, props.country) &&
                  thisRowControlsCells.push(
                    <Grid.Item
                      size={{ xs: eachRowControl.width }}
                      className="hc8-pt-none"
                    >
                      {generateSubRows(
                        source,
                        subSectionTemplate,
                        mutatedRowControl,
                        subSectionData,
                        subSectionRowIndex,
                      )}
                      {eachRowControl.add_more && (
                        <Button
                          data-testid={`btn-add-more-composite-${eachRowControl.type}`}
                          key={`btn-add-more-composite-${eachRowControl.type}`}
                          variant="ghost"
                          className="hc8-pl-none hc8-mt-2x"
                          onClick={() => {
                            addNewSubRow(
                              subSectionTemplate.uid,
                              eachRowControl, // entire composite control
                              subSectionRowIndex, // subsection index
                            )
                          }}
                        >
                          <Icon svg={PlusCircleIcon} className="hc8-mr-2x" />
                          {eachRowControl.add_more?.label}
                        </Button>
                      )}
                    </Grid.Item>,
                  )
                break
              default: {
                //Normal controls (textbox, dropdown etc)
                let control = renderControlView(
                  mutatedRowControl, //eachRowControl
                  source === SOURCE_SUBSECTION ? subSectionData : sectionData,
                  props.rfiMasterData,
                  onInputChangeHandler,
                  props.country,
                )
                if (control) {
                  thisRowControlsCells.push(
                    <Grid.Item size={{ xs: eachRowControl.width }}>
                      {control}
                    </Grid.Item>,
                  )
                }
              }
            }
          })

          if (thisRowControlsCells.length !== 0) {
            let rowSpacingClass = ''
            if (eachRowIndex > 0) {
              rowSpacingClass = props.isReadonly ? 'hc8-mt-3x' : 'hc8-mt-4x'
            }

            allRows.push(
              <Grid.Container className={rowSpacingClass}>
                {thisRowControlsCells}
              </Grid.Container>,
            )
          }
        })
    }
    return allRows
  }

  //#region Type3-(Nested subsection)

  /**
   * Generate all subrows for a composite control (Type 3)
   * @param {*} source
   * @param {*} subSectionTemplate
   * @param {*} repeaterControlTemplate
   * @param {*} subSectionData
   * @param {*} subSectionRowIndex
   * @returns
   */
  const generateSubRows = (
    source, // CONTROL / SUBSECTION,
    subSectionTemplate,
    repeaterControlTemplate,
    subSectionData,
    subSectionRowIndex,
  ) => {
    let repeaterUid = repeaterControlTemplate.uid
    let repeaterData = subSectionData[repeaterUid]
    let thisSubrowControls = repeaterControlTemplate.controls
    let allRepeatingRows = []

    //1. Loop subrow data times for composite control row replication
    repeaterData?.forEach((eachRepeaterData, eachSubRowIndex) => {
      let eachT3Row = []
      let runningWidth = 0
      thisSubrowControls.forEach((eachSubRowControl) => {
        //Each control in subrow - prepare metadata
        let mutatedSubRowControl = Object.assign({}, eachSubRowControl)
        let subControlMetadata = {}
        subControlMetadata.source = source
        subControlMetadata.sectionPath = `${repeaterControlTemplate.metadata.sectionPath}.${repeaterUid}[${eachSubRowIndex}]`
        subControlMetadata.errorCache = props.errorCache
        subControlMetadata.sectionUid = props.sectionTemplate?.uid
        if (source === SOURCE_SUBSECTION) {
          subControlMetadata.subSectionReferenceListKey = subSectionTemplate.uid
          subControlMetadata.index = subSectionRowIndex
          subControlMetadata.subControlReferenceListKey = repeaterUid
          subControlMetadata.subControlIndex = eachSubRowIndex
        }
        mutatedSubRowControl.metadata = subControlMetadata

        //Merge eachRepeaterData + Parent's data (might be needed in subrow formation)
        let repeaterDataWithParent = { ...eachRepeaterData, ...subSectionData }
        let resolvedSubRowControl = renderControlView(
          mutatedSubRowControl, //control + metadata
          repeaterDataWithParent, //data for composite control
          props.rfiMasterData,
          onInputChangeHandler,
          props.country,
        )
        if (resolvedSubRowControl) {
          eachT3Row.push(
            <Grid.Item
              size={{ xs: eachSubRowControl.width }}
              className="hc8-pa-none"
            >
              {resolvedSubRowControl}
            </Grid.Item>,
          )
          runningWidth = runningWidth + Number(eachSubRowControl.width)
        }
      })

      eachT3Row.push(
        <Grid.Item
          size={{ xs: 12 - (runningWidth % 12) }}
          className="row-top hc8-pa-none"
        >
          {repeaterControlTemplate &&
            repeaterControlTemplate.remove &&
            eachSubRowIndex !== 0 &&
            (repeaterControlTemplate.remove?.type?.toUpperCase() === BUTTON ? (
              <Button
                data-testid={`button-delete-row-${eachSubRowIndex}`}
                key={`button-delete-row-${eachSubRowIndex}`}
                className="hc8-mt-6x"
                onClick={() =>
                  deleteSubRow(
                    subSectionTemplate.uid,
                    repeaterControlTemplate,
                    subSectionRowIndex,
                    eachSubRowIndex,
                  )
                }
                variant="secondary"
              >
                Remove
              </Button>
            ) : (
              <Button
                data-testid={`icon-delete-row-${eachSubRowIndex}`}
                key={`icon-delete-row-${eachSubRowIndex}`}
                className="hc8-mt-6x"
                onClick={() =>
                  deleteSubRow(
                    subSectionTemplate.uid,
                    repeaterControlTemplate,
                    subSectionRowIndex,
                    eachSubRowIndex,
                  )
                }
                variant="ghost"
                size="sm"
                isIconOnly
                aria-label="delete row"
              >
                <Icon svg={MinusCircleIcon} />
              </Button>
            ))}
        </Grid.Item>,
      )
      allRepeatingRows.push(
        <Grid.Container className="hc8-mh-none hc8-mt-none background-grey border-radius hc8-mb-2x hc8-pa-3x">
          {eachT3Row}
        </Grid.Container>,
      )
    })

    return (
      <Grid.Container spacing="none" className="hc8-mt-1x">
        {allRepeatingRows}
      </Grid.Container>
    )
  }

  /**
   * Add new subrow in composite control
   * @param {*} subSectionKey
   * @param {*} subRowControl
   * @param {*} subSectionRowIndex
   */
  const addNewSubRow = (subSectionKey, subRowControl, subSectionRowIndex) => {
    let subControlKey = subRowControl.uid

    //Find matching subsection
    let matchingSubSectionTemplate = props.sectionTemplate?.sub_sections.filter(
      (eachSubsection) => eachSubsection.uid === subSectionKey,
    )
    if (matchingSubSectionTemplate?.length > 0) {
      let subNodeToAdd = {}
      matchingSubSectionTemplate[0].data.controls
        .filter((eachControl) => eachControl.uid === subControlKey)[0]
        ?.controls.map(
          (eachSubControl) =>
            (subNodeToAdd[eachSubControl.uid] =
              getNodeToAddByControlType(eachSubControl)),
        )

      let newSubSectionData = sectionData[subSectionKey].map(
        (eachSubSectionRow, eachSubSectionIndex) => {
          return eachSubSectionIndex !== subSectionRowIndex
            ? eachSubSectionRow
            : {
                ...eachSubSectionRow,
                [subControlKey]: [
                  ...eachSubSectionRow[subControlKey],
                  subNodeToAdd,
                ],
              }
        },
      )
      setSectionData({
        ...sectionData,
        [subSectionKey]: newSubSectionData,
      })
    }
  }

  /**
   * Delete composite control row
   * @param {*} subSectionKey
   * @param {*} subRowControl
   * @param {*} subSectionRowIndex
   * @param {*} subRowIndexToDelete
   */
  const deleteSubRow = (
    subSectionKey,
    subRowControl, //compsite control
    subSectionRowIndex, //subsection index
    subRowIndexToDelete, //composite index
  ) => {
    let subControlKey = subRowControl.uid
    //Find matching subsection
    let matchingSubSectionTemplate = props.sectionTemplate?.sub_sections.filter(
      (eachSubsection) => eachSubsection.uid === subSectionKey,
    )
    if (matchingSubSectionTemplate?.length > 0) {
      //Find matching control
      let newSubSectionData = sectionData[subSectionKey].map(
        (eachSubSectionRow, eachSubSectionIndex) => {
          if (eachSubSectionIndex !== subSectionRowIndex) {
            return eachSubSectionRow
          } else {
            let newSubSectionRows = [...eachSubSectionRow[subControlKey]]
            newSubSectionRows.splice(subRowIndexToDelete, 1)
            return { ...eachSubSectionRow, [subControlKey]: newSubSectionRows }
          }
        },
      )

      setSectionData({
        ...sectionData,
        [subSectionKey]: newSubSectionData,
      })
    }
  }

  /**
   * Render add more for subsection
   * @param {*} subSectionReferenceKey
   * @returns
   */
  const renderAddMoreSubSection = (subSectionReferenceKey) => {
    let matchingSubSectionTemplate =
      props.sectionTemplate?.sub_sections?.filter(
        (eachSubsection) => eachSubsection.uid === subSectionReferenceKey,
      )

    if (
      matchingSubSectionTemplate?.length > 0 &&
      matchingSubSectionTemplate[0].add_more &&
      matchingSubSectionTemplate[0].add_more.type.toUpperCase() === TEXT
    ) {
      return (
        <Button
          data-testid={`btn-add-more-${subSectionReferenceKey}`}
          key={`btn-add-more-${subSectionReferenceKey}`}
          variant="ghost"
          className="hc8-pl-none hc8-mt-4x"
          onClick={() => {
            addNewSubsectionRow(subSectionReferenceKey)
          }}
        >
          <Icon svg={PlusCircleIcon} className="hc8-mr-2x" />
          {matchingSubSectionTemplate[0].add_more?.label}
        </Button>
      )
    }
  }

  /**
   * Add a row inside a subsection
   * @param {*} subSectionReferenceKey
   */
  const addNewSubsectionRow = (subSectionReferenceKey) => {
    //Find matching subsection
    let matchingSubSectionTemplate = props.sectionTemplate?.sub_sections.filter(
      (eachSubsection) => eachSubsection.uid === subSectionReferenceKey,
    )
    if (matchingSubSectionTemplate?.length > 0) {
      //Show configured toast message (if present)
      if (matchingSubSectionTemplate[0].add_more?.toast_message) {
        makeToast({
          variant: 'success',
          heading: matchingSubSectionTemplate[0].add_more?.toast_message,
          message: '',
          autoHideDuration: 5000,
        })
      }

      let nodeToAdd = {}
      matchingSubSectionTemplate[0].data.controls.forEach((eachControl) => {
        if (eachControl.type === 'inside_row_repeater') {
          //Create 1st subControl row when adding entire subsection row
          //Parse inside_row_repeater's control
          let subNodeToAdd = {}
          eachControl.controls.map(
            (eachSubControl) =>
              (subNodeToAdd[eachSubControl.uid] =
                getNodeToAddByControlType(eachSubControl)),
          )
          nodeToAdd[eachControl.uid] = [subNodeToAdd]
        } else {
          nodeToAdd[eachControl.uid] = getNodeToAddByControlType(eachControl)
        }
      })

      setSectionData({
        ...sectionData,
        [subSectionReferenceKey]: [
          ...sectionData[subSectionReferenceKey],
          nodeToAdd,
        ],
      })
      //For row additions perisit the row to DB immediately when added
      setIsTriggerSave(true)
    }
  }

  //#endregion

  /**
   * Get node to add by control type
   * Details: When adding subrow, we read typical subsection from subsection data -> controls
   * For each control based on the type we either determine if its a string, number, array, object etc
   * Together they form the new subsection row node comprising of all these control's data types
   * @param {*} control
   * @returns
   */
  const getNodeToAddByControlType = (control) => {
    switch (control.type) {
      case CONTROL_TYPE_FILEUPLOAD:
        return {
          uploaded_files: {},
        }
      case CONTROL_TYPE_STRENGTH_ASSESSMENT:
        return {
          segments: [],
        }
      case CONTROL_TYPE_MULTI_AUTOCOMPLETE:
      case CONTROL_TYPE_MULTI_AUTOCOMPLETE_OTHERS:
      case CONTROL_TYPE_MULTI_AUTOCOMPLETE_HIERARCHY:
        return []
      default:
        return ''
    }
  }

  //#region File Upload Zone

  /**
   * Upload file
   * NOTE: index of upload is sent via metadata
   * For Type 1 (array of controls) -> send index = -1 to upload API
   * For Type 2 (list of subsections) -> send index = subsection index to upload API [since each row has a upload]
   * @param {*} files
   * @param {*} uid
   * @param {*} componentName
   * @param {*} metadata
   * @returns
   */
  const uploadSectionFile = async (files, uid, componentName, metadata) => {
    let indexOfUpload = metadata.index >= 0 ? metadata.index : -1 //If subsection level upload send index , else -1
    try {
      setFileActionInProgressKey(metadata.fileActionKey)
      setFileActionFailedKey(null)

      //1. Upload file(s)
      let response = await uploadFiles(
        props.rfiType,
        componentName,
        false,
        files,
        indexOfUpload,
        props.supplierId,
        props.locationId,
        props.manufacturingTypeId,
      )

      // 2. Post upload success - update state
      if (indexOfUpload === -1) {
        //a> Type 1 section upload
        let newFileSet = {
          ...sectionData[uid].uploaded_files,
          ...response.files,
        }
        setSectionData({
          ...sectionData,
          [uid]: {
            uploaded_files: newFileSet,
          },
        })
      } else {
        //b> Type 2 section upload
        let newSectionData = sectionData[
          metadata.subSectionReferenceListKey
        ].map((eachSubsection, subsectionIndex) =>
          subsectionIndex !== indexOfUpload
            ? eachSubsection
            : {
                ...eachSubsection,
                [uid]: {
                  uploaded_files: {
                    ...eachSubsection[uid].uploaded_files,
                    ...response.files,
                  },
                },
              },
        )
        setSectionData({
          ...sectionData,
          [metadata.subSectionReferenceListKey]: newSectionData,
        })
      }
      setFileActionInProgressKey(null)
      setFileActionFailedKey(null)
      return response
    } catch (error) {
      setFileActionInProgressKey(null)
      setFileActionFailedKey(metadata.fileActionKey)
      return error
    }
  }

  /**
   * Download File - by filename, uid, componentName = path of file, index of subsection row
   * @param {*} fileName
   * @param {*} tossKey
   * @param {*} componentName
   * @param {*} index
   * @returns
   */
  const downloadSectionFile = async (
    fileName,
    tossKey,
    componentName,
    metadata = {},
  ) => {
    try {
      setFileActionInProgressKey(metadata.fileActionKey)
      setFileActionFailedKey(null)
      let response = await downloadFile(
        props.rfiType,
        componentName,
        fileName,
        tossKey,
        metadata.index >= 0 ? metadata.index : -1,
      )
      setFileActionInProgressKey(null)
      setFileActionFailedKey(null)
      return response
    } catch (error) {
      setFileActionInProgressKey(null)
      setFileActionFailedKey(null)
      return error
    }
  }

  /**
   * Delete File - by filename, uid, componentName = path of file, index of subsection row
   * NOTE: index of delete is sent via metadata
   * For Type 1 (array of controls) -> send index = -1 to delete API
   * For Type 2 (list of subsections) -> send index = subsection index to delete API [since each row has a upload]
   * @param {*} fileName
   * @param {*} uid
   * @param {*} componentName
   * @param {*} metadata
   * @returns
   */
  const deleteSectionFile = async (fileName, uid, componentName, metadata) => {
    let indexOfDeletion = metadata.index >= 0 ? metadata.index : -1 //If subsection level upload send index , else -1

    try {
      setFileActionInProgressKey(metadata.fileActionKey)
      setFileActionFailedKey(null)

      //1. Delete file
      let response = await deleteFile(
        props.rfiType,
        componentName,
        fileName,
        indexOfDeletion,
        props.supplierId,
        props.locationId,
        props.manufacturingTypeId,
      )
      // 2. Post delete success - update state
      if (indexOfDeletion === -1) {
        //a> Type 1 section file deletion
        let fileList = { ...sectionData[uid].uploaded_files }
        delete fileList[fileName]
        setSectionData({
          ...sectionData,
          [uid]: {
            uploaded_files: fileList,
          },
        })
      } else {
        //b> Type 2 section file deletion
        let newSectionData = sectionData[
          metadata.subSectionReferenceListKey
        ].map((eachSubsection, subsectionIndex) => {
          if (subsectionIndex !== indexOfDeletion) {
            return eachSubsection
          } else {
            let fileList = { ...eachSubsection[uid].uploaded_files }
            delete fileList[fileName]
            return { ...eachSubsection, [uid]: { uploaded_files: fileList } }
          }
        })
        setSectionData({
          ...sectionData,
          [metadata.subSectionReferenceListKey]: newSectionData,
        })
      }

      setFileActionInProgressKey(null)
      setFileActionFailedKey(null)
      return response
    } catch (error) {
      setFileActionInProgressKey(null)
      setFileActionFailedKey(metadata.fileActionKey)
      return error
    }
  }

  //#endregion

  return props.isHeaderSection ? (
    <div>{renderSectionContent()}</div>
  ) : (
    props.sectionTemplate?.order !== 0 && (
      <SectionWrapper
        sectionTemplate={props.sectionTemplate}
        sectionBody={renderSectionContent()}
        setIsTriggerSave={
          !props.isReadonly || props.isRfi3 ? setIsTriggerSave : () => {}
        }
        sectionPath={props.sectionPath}
        errorCache={props.errorCache}
        isRfi3={props.isRfi3}
        addMoreMetadata={{
          isAddMore: topAddEligibleSubSection?.add_more,
          addMoreButtonText: topAddEligibleSubSection?.add_more.label,
          addMoreSubsectionReferenceKey: topAddEligibleSubSection?.uid,
          addMore: (key) => addNewSubsectionRow(key),
        }}
      />
    )
  )
}

export default DynamicSectionBuilder
